import { useMemo } from 'react'
import { Copy, Code } from 'lucide-react'
import type { CanvasBlock } from '@/lib/cms/card-registry'
import { getCardDef } from '@/lib/cms/card-registry'

/* ── Props ───────────────────────────────────────────── */

interface CodeInspectorProps {
  block: CanvasBlock | null
}

/* ── Helpers ─────────────────────────────────────────── */

function generateHTML(block: CanvasBlock): string {
  const def = getCardDef(block.type)
  const tag = block.type === 'heading' ? (block.data?.level || 'h2') : 'div'
  const className = `cms-block cms-${block.type}`

  let inner = ''
  if (block.data?.headline) inner += `  <h1>${block.data.headline}</h1>\n`
  if (block.data?.heading) inner += `  <h2>${block.data.heading}</h2>\n`
  if (block.data?.subline) inner += `  <p class="subline">${block.data.subline}</p>\n`
  if (block.data?.body) inner += `  <p>${block.data.body}</p>\n`
  if (block.data?.text) inner += `  ${block.data.text}\n`
  if (block.data?.src) inner += `  <img src="${block.data.src}" alt="${block.data.alt || ''}" />\n`
  if (block.data?.cta_text) inner += `  <a href="${block.data.cta_link || '#'}" class="cta">${block.data.cta_text}</a>\n`
  if (block.data?.name) inner += `  <span class="name">${block.data.name}</span>\n`
  if (block.data?.quote) inner += `  <blockquote>${block.data.quote}</blockquote>\n`

  if (!inner) inner = `  <!-- ${def?.label || block.type} content -->\n`

  return `<${tag} class="${className}"\n  data-block-id="${block.id}"\n  data-col-span="${block.colSpan}">\n${inner}</${tag}>`
}

function generateCSS(block: CanvasBlock): string {
  const s = block.styles
  const lines: string[] = [`.cms-${block.type}[data-block-id="${block.id}"] {`]

  lines.push(`  grid-column: span ${block.colSpan};`)

  if (s) {
    if (s.backgroundColor) lines.push(`  background-color: ${s.backgroundColor};`)
    if (s.textColor) lines.push(`  color: ${s.textColor};`)
    if (s.fontFamily) lines.push(`  font-family: "${s.fontFamily}", sans-serif;`)
    if (s.fontSize) lines.push(`  font-size: ${s.fontSize}px;`)
    if (s.fontWeight) lines.push(`  font-weight: ${s.fontWeight};`)
    if (s.textAlign) lines.push(`  text-align: ${s.textAlign};`)
    if (s.borderRadius !== undefined) lines.push(`  border-radius: ${s.borderRadius}px;`)
    if (s.borderColor) lines.push(`  border: ${s.borderWidth || 1}px solid ${s.borderColor};`)
    if (s.padding !== undefined) lines.push(`  padding: ${s.padding}px;`)
    if (s.opacity !== undefined) lines.push(`  opacity: ${s.opacity};`)
    if (s.shadow && s.shadow !== 'none') lines.push(`  box-shadow: var(--shadow-${s.shadow});`)
  }

  if (block.customCss) {
    lines.push(``)
    lines.push(`  /* Custom CSS */`)
    block.customCss.split('\n').forEach(l => lines.push(`  ${l}`))
  }

  lines.push('}')
  return lines.join('\n')
}

/* ── Component ───────────────────────────────────────── */

export default function CodeInspector({ block }: CodeInspectorProps) {
  const html = useMemo(() => block ? generateHTML(block) : '', [block])
  const css = useMemo(() => block ? generateCSS(block) : '', [block])

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).catch(() => {})
  }

  if (!block) {
    return (
      <div className="editor-empty-state">
        <Code size={24} className="editor-empty-state-icon" />
        <div className="editor-empty-state-text">Select a block to inspect</div>
        <div className="editor-empty-state-hint">View the generated HTML and CSS</div>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'auto' }}>
      {/* HTML section */}
      <div className="editor-section-header">
        <span style={{ flex: 1 }}>HTML</span>
        <button
          className="editor-btn editor-btn-ghost editor-btn-icon editor-btn-sm"
          onClick={() => copyToClipboard(html)}
          title="Copy HTML"
        >
          <Copy size={10} />
        </button>
      </div>
      <pre className="editor-code-view" style={{ margin: '0 8px 8px' }}>
        {html}
      </pre>

      {/* CSS section */}
      <div className="editor-section-header">
        <span style={{ flex: 1 }}>CSS</span>
        <button
          className="editor-btn editor-btn-ghost editor-btn-icon editor-btn-sm"
          onClick={() => copyToClipboard(css)}
          title="Copy CSS"
        >
          <Copy size={10} />
        </button>
      </div>
      <pre className="editor-code-view" style={{ margin: '0 8px 8px' }}>
        {css}
      </pre>
    </div>
  )
}
